import { api } from './http';

/**
 * Execute a DDL statement (e.g., CREATE OR REPLACE VIEW) via backend endpoint.
 * Only allowed for admin/trusted users.
 */
export const execDDL = async (query_string: string) => {
  const response = await api.post('/dbExchange/execddl', { query_string });
  return response.data;
};

export const importCsvExcel = async (file: File, tableName: string, ifExists: 'append' | 'replace' | 'fail' = 'append'): Promise<any> => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('table_name', tableName);
  formData.append('if_exists', ifExists);
  const response = await api.post('/dbExchange/csv_excel_import', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    timeout: 60000
  });
  return response.data;
};

/**
 * VPS sunucusundaki CSV/Excel dosyasını veritabanına import eder
 * @param filePath - VPS'teki dosya yolu (örn: "temp/data.xlsx" veya "Book2.csv")
 * @param tableName - Hedef veritabanı tablosu
 * @param ifExists - 'append' | 'replace' | 'fail'
 */
export const importCsvExcelFromServer = async (
  filePath: string,
  tableName: string,
  ifExists: 'append' | 'replace' | 'fail' = 'append'
): Promise<any> => {
  const formData = new FormData();
  formData.append('file_path', filePath);
  formData.append('table_name', tableName);
  formData.append('if_exists', ifExists);
  const response = await api.post('/dcc/csv_import', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    timeout: 300000 // 5 dakika - büyük dosyalar için
  });
  return response.data;
};

/**
 * DCC üzerinde bulunan bir CSV dosyasını (dosya yolu ile) backend üzerinden import eder.
 *
 * NOT: Bu endpoint sadece CSV destekler. XLS/XLSX için `importCsvExcelFromServer` tercih edilmelidir.
 */
export const csvImportFromDccPath = async (args: {
  dccPath: string;
  tableName: string;
  ifExists?: 'append' | 'replace' | 'fail';
}): Promise<any> => {
  const response = await api.post(
    '/dbExchange/csvimport_from_dcc',
    {
      dcc_path: args.dccPath,
      table_name: args.tableName,
      if_exists: args.ifExists || 'append',
    },
    {
      headers: { 'Content-Type': 'application/json' },
      timeout: 300000,
    }
  );
  return response.data;
};

/**
 * Upload edilen CSV/Excel dosyasını DCC üzerinden veritabanına import eder
 * @param file - Upload edilecek dosya
 * @param tableName - Hedef veritabanı tablosu
 * @param ifExists - 'append' | 'replace' | 'fail'
 */
export const importCsvExcelViaDCC = async (
  file: File,
  tableName: string,
  ifExists: 'append' | 'replace' | 'fail' = 'append'
): Promise<any> => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('table_name', tableName);
  formData.append('if_exists', ifExists);
  const response = await api.post('/dcc/csv_import', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    timeout: 300000 // 5 dakika - büyük dosyalar için
  });
  return response.data;
};

// ---------------------------------------------------------------------------
// Personel Excel toplu import (analyze -> önizleme -> commit)
// Backend: services/routes/personnel_import_endpoints.py
// ---------------------------------------------------------------------------

export interface PersonnelImportPersonBrief {
  id: number;
  pers_id: string | null;
  /** SAP personel no (Excel "Personel No"); pers_id GAP sicildir, import dokunmaz */
  sap_no?: string | null;
  first_name: string;
  last_name: string;
  email: string | null;
  company_id: number | null;
  department: string | null;
  position: string | null;
  employment_status: string;
  /** Bulanık isim eşleşmesinde 0-1 arası benzerlik skoru */
  benzerlik?: number;
}

export type PersonnelImportRowAction =
  | 'insert'
  | 'update'
  | 'unchanged'
  | 'conflict'
  /** Excel Status=Inactive/Terminated: mevcut kayıt terminated yapılır */
  | 'terminate'
  | 'warning'
  | 'error';

export interface PersonnelImportPlanRow {
  row_no: number;
  excel: Record<string, any>;
  messages: string[];
  action: PersonnelImportRowAction;
  target_id: number | null;
  diff: Record<string, { eski: any; yeni: any }> | null;
  candidates: PersonnelImportPersonBrief[];
}

export interface PersonnelImportPlan {
  rows: PersonnelImportPlanRow[];
  terminate_candidates: PersonnelImportPersonBrief[];
  summary: Record<string, number>;
  file_name: string;
}

export type PersonnelImportAction =
  | { type: 'insert'; data: Record<string, any> }
  | { type: 'update'; id: number; data: Record<string, any> }
  | { type: 'terminate'; id: number; data?: Record<string, any> };

export interface PersonnelImportCommitResult {
  success: boolean;
  log_id?: number;
  counts?: { insert: number; update: number; terminate: number; skip: number };
  error?: string;
}

export const personnelImportAnalyze = async (file: File): Promise<PersonnelImportPlan> => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await api.post('/dbExchange/personnel_import/analyze', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120000,
  });
  return response.data as PersonnelImportPlan;
};

export const personnelImportCommit = async (payload: {
  file_name: string;
  actions: PersonnelImportAction[];
}): Promise<PersonnelImportCommitResult> => {
  const response = await api.post('/dbExchange/personnel_import/commit', payload, {
    headers: { 'Content-Type': 'application/json' },
    timeout: 120000,
  });
  return response.data as PersonnelImportCommitResult;
};

// Trigger server-side copy from SQL Server to PostgreSQL
export const copyTableFromSqlToPg = async (payload: {
  source_table: string;
  dest_table: string;
  source_database?: 'DiAPCQS' | 'PPMTRAIN';
  column_mapping?: Record<string, string>;
  where_clause?: string;
  truncate_dest?: boolean;
  batch_size?: number;
}) => {
  const response = await api.post('/admin/copy-table', payload, {
    headers: { 'Content-Type': 'application/json' },
    timeout: 0,
  });
  return response.data as { success: boolean; stats?: { source_rows: number; inserted_rows: number }; error?: string };
};
